import { useLanguage } from '@/context/LanguageContext';
import { Link } from 'react-router-dom';
import { Eye, Rocket, Star, Handshake, Lock, GitBranch, ArrowRight, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEOHead from '@/components/SEOHead';
import { CornerAccents } from '@/components/CornerAccents';
import { Reveal } from '@/components/motion/Reveal';
import { SpotlightCard } from '@/components/motion/SpotlightCard';
import { CountUp } from '@/components/motion/CountUp';

const HEADING_FONT = { fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" };
const BODY_FONT = { fontFamily: "'Manrope', 'Noto Sans TC', sans-serif" };

const VALUES = [
  { icon: Eye, key: 'value_user' },
  { icon: Rocket, key: 'value_innovation' },
  { icon: Star, key: 'value_excellence' },
  { icon: Handshake, key: 'value_collaboration' },
];

function BrowserChrome() {
  return (
    <div className="h-6 md:h-7 flex items-center gap-1.5 px-3 bg-[#131c30]">
      <span className="w-2 h-2 rounded-full bg-[#ff5f57]" />
      <span className="w-2 h-2 rounded-full bg-[#febc2e]" />
      <span className="w-2 h-2 rounded-full bg-[#28c840]" />
    </div>
  );
}

function EcosystemCollage() {
  const { t } = useLanguage();
  return (
    <div
      data-testid="about-ecosystem-collage"
      className="relative rounded-3xl bg-[#0E131F] border border-slate-800/60 p-5 md:p-7 overflow-hidden shadow-xl"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(360px circle at 20% 15%, rgba(99,102,241,0.10), transparent 60%), radial-gradient(420px circle at 85% 65%, rgba(99,102,241,0.12), transparent 60%)',
        }}
      />
      <p className="relative z-10 text-[10px] md:text-xs uppercase tracking-[0.25em] font-bold text-[#64748B] mb-4">
        {t('about.eco_caption')}
      </p>

      <div className="relative z-10 w-[84%] rounded-xl overflow-hidden border border-white/10 shadow-lg -rotate-2 hover:rotate-0 transition-transform duration-500">
        <BrowserChrome />
        <img src="/press-kit/vowpace-og.jpg" alt="Vowpace AI preview" data-testid="about-eco-vowpace" className="w-full object-cover" />
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2">
          <p className="text-xs font-bold text-white">Vowpace AI <span className="ml-1 font-normal text-[#6366F1]/90">{t('about.eco_vowpace_tag')}</span></p>
        </div>
      </div>

      <div className="relative z-20 w-[84%] ml-auto -mt-6 md:-mt-8 rounded-xl overflow-hidden border border-[#818CF8]/30 shadow-[0_16px_40px_rgba(129,140,248,0.25)] rotate-2 hover:rotate-0 transition-transform duration-500">
        <BrowserChrome />
        <img src="/press-kit/lcshield-og.png" alt="LC Shield preview" data-testid="about-eco-lcshield" className="w-full object-cover" />
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2">
          <p className="text-xs font-bold text-white flex items-center gap-1.5">
            <img src="/press-kit/lcshield-logo.png" alt="LC Shield logo" className="w-4 h-4 object-contain" style={{ filter: 'drop-shadow(0 0 4px rgba(129,140,248,0.8))' }} />
            LC Shield <span className="font-normal text-[#0A84FF]">{t('about.eco_lc_tag')}</span>
          </p>
        </div>
      </div>

      <div className="relative z-10 mt-5 flex flex-wrap gap-2">
        {['UCP 600', 'ISBP 821 (2023)', 'US SUBCONTRACTS', 'MT700'].map((rule) => (
          <span key={rule} className="px-3 py-1.5 rounded-full bg-white/[0.04] border border-slate-800/60 text-[10px] font-bold tracking-wider text-[#64748B]">
            {rule}
          </span>
        ))}
      </div>
    </div>
  );
}

function StorySection() {
  const { t } = useLanguage();
  return (
    <section data-testid="story-section" className="py-24 md:py-32 bg-[#080B10]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#6366F1] mb-4">
              {t('about.story_label')}
            </p>
            <h1
              data-testid="about-title"
              className="text-4xl md:text-5xl lg:text-6xl tracking-tighter font-bold text-[#F8FAFC] leading-tight mb-8"
              style={HEADING_FONT}
            >
              {t('about.title')}
            </h1>
            <div className="space-y-6">
              <p data-testid="about-story" className="text-base md:text-lg leading-relaxed text-[#94A3B8]" style={BODY_FONT}>
                {t('about.story')}
              </p>
              <p data-testid="about-story-2" className="text-base md:text-lg leading-relaxed text-[#94A3B8]" style={BODY_FONT}>
                {t('about.story2')}
              </p>
            </div>
          </div>
          <div className="md:col-span-6">
            <EcosystemCollage />
          </div>
        </div>
      </div>
    </section>
  );
}

function RadarMockup({ t }) {
  return (
    <div className="flex items-center gap-6 md:gap-10 mt-8">
      <div className="relative w-36 h-36 md:w-44 md:h-44 flex-shrink-0">
        {[1, 0.72, 0.44].map((r) => (
          <span
            key={r}
            className="absolute rounded-full border border-[#6366F1]/25"
            style={{ inset: `${(1 - r) * 50}%` }}
          />
        ))}
        <span className="absolute inset-0 rounded-full overflow-hidden">
          <span
            className="absolute inset-0"
            style={{
              background: 'conic-gradient(from 0deg, rgba(99,102,241,0.45), transparent 70deg)',
              animation: 'radar-sweep 4s linear infinite',
            }}
          />
        </span>
        <span className="absolute w-1.5 h-1.5 rounded-full bg-[#F59E0B]" style={{ top: '28%', left: '62%', animation: 'blip 2.2s ease-in-out infinite' }} />
        <span className="absolute w-1.5 h-1.5 rounded-full bg-[#818CF8]" style={{ top: '58%', left: '34%', animation: 'blip 3s ease-in-out infinite 0.6s' }} />
        <span className="absolute w-1.5 h-1.5 rounded-full bg-[#F59E0B]" style={{ top: '70%', left: '66%', animation: 'blip 2.6s ease-in-out infinite 1.1s' }} />
        <span className="absolute inset-[46%] rounded-full bg-[#6366F1] shadow-[0_0_12px_rgba(99,102,241,0.8)]" />
      </div>
      <div className="flex-1 space-y-2.5 min-w-0">
        {[
          { w: 'w-11/12', tag: '§8.7', color: '#F59E0B' },
          { w: 'w-3/4', tag: 'ART.14', color: '#818CF8' },
          { w: 'w-5/6', tag: '§9.3 PAY-IF-PAID', color: '#F59E0B' },
          { w: 'w-2/3', tag: 'OK', color: '#10B981' },
        ].map((row) => (
          <div key={row.tag} className="flex items-center gap-3">
            <span className={`h-1.5 rounded-full bg-white/[0.07] ${row.w}`} />
            <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded flex-shrink-0" style={{ color: row.color, backgroundColor: `${row.color}14`, border: `1px solid ${row.color}35` }}>
              {row.tag}
            </span>
          </div>
        ))}
        <p className="text-[10px] font-mono uppercase tracking-widest text-[#64748B] pt-1">
          {t('about.bento1_scan')} <span className="text-[#F59E0B]">▮▮▮</span>
        </p>
      </div>
    </div>
  );
}

function ExpertiseSection() {
  const { t } = useLanguage();
  return (
    <section data-testid="expertise-section" className="py-24 md:py-32 bg-[#080B10] border-t border-slate-800/60">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="text-center mb-16">
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#818CF8]">
            {t('about.expertise_label')}
          </p>
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Reveal className="lg:col-span-2 lg:row-span-2">
          <SpotlightCard className="rounded-3xl h-full">
          <div
            data-testid="bento-zone-1"
            className="relative h-full rounded-3xl bg-[#0E131F] border border-slate-800/60 p-8 md:p-12 overflow-hidden"
          >
            <div className="absolute inset-0">
              <img src="/img/command-panel.jpg" alt="" aria-hidden className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-[#0E131F]/[0.88]" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0E131F] via-transparent to-[#0E131F]/70" />
            </div>
            <CornerAccents />
            <div className="relative z-10">
              <span className="inline-block px-2.5 py-1 rounded font-mono text-[10px] font-bold tracking-widest text-[#6366F1] bg-[#6366F1]/10 border border-[#6366F1]/30 mb-6">
                CORE ENGINE
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] tracking-tight mb-4" style={HEADING_FONT}>
                {t('about.bento1_t')}
              </h3>
              <p className="text-sm md:text-base text-[#94A3B8] leading-relaxed max-w-xl" style={BODY_FONT}>
                {t('about.bento1_d')}
              </p>
              <RadarMockup t={t} />
            </div>
          </div>
          </SpotlightCard>
          </Reveal>

          <Reveal delay={0.12}>
          <SpotlightCard glow="#F59E0B" className="rounded-3xl h-full">
          <div
            data-testid="bento-zone-2"
            className="relative h-full rounded-3xl bg-[#0E131F] border border-slate-800/60 p-8 overflow-hidden"
          >
            <CornerAccents color="#F59E0B" />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'radial-gradient(280px circle at 80% 15%, rgba(245,158,11,0.10), transparent 60%)' }}
            />
            <div className="relative z-10">
              <div className="relative w-16 h-16 mb-5">
                <span className="absolute inset-0 rounded-full border border-[#F59E0B]/30" />
                <span className="absolute inset-2 rounded-full border border-[#F59E0B]/50" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <Lock className="h-6 w-6 text-[#F59E0B]" />
                </span>
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-[#F8FAFC] mb-2" style={HEADING_FONT}>
                {t('about.bento2_t')}
              </h3>
              <p className="text-sm text-[#94A3B8] leading-relaxed mb-4" style={BODY_FONT}>{t('about.bento2_d')}</p>
              <span className="inline-block px-2.5 py-1 rounded font-mono text-[10px] font-bold tracking-widest text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30">
                ENCRYPTED IN TRANSIT (HTTPS)
              </span>
            </div>
          </div>
          </SpotlightCard>
          </Reveal>

          <Reveal delay={0.2}>
          <SpotlightCard className="rounded-3xl h-full">
          <div
            data-testid="bento-zone-3"
            className="relative h-full rounded-3xl bg-[#0E131F] border border-slate-800/60 p-8 overflow-hidden"
          >
            <CornerAccents />
            <div className="relative z-10">
              <GitBranch className="h-6 w-6 text-[#6366F1] mb-5" />
              <h3 className="text-lg md:text-xl font-semibold text-[#F8FAFC] mb-2" style={HEADING_FONT}>
                {t('about.bento3_t')}
              </h3>
              <p className="text-sm text-[#94A3B8] leading-relaxed mb-6" style={BODY_FONT}>{t('about.bento3_d')}</p>
              <div className="relative h-10">
                <span className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-[#6366F1]/60 via-[#6366F1]/30 to-slate-800" />
                {[0, 30, 62, 92].map((x, i) => (
                  <span
                    key={x}
                    className={`absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full border-2 ${i < 2 ? 'bg-[#6366F1] border-[#6366F1]' : 'bg-[#0E131F] border-[#6366F1]/40'}`}
                    style={{ left: `${x}%` }}
                  />
                ))}
                <span className="absolute -top-1 font-mono text-[9px] text-[#F59E0B]" style={{ left: '30%' }}>D+14</span>
              </div>
            </div>
          </div>
          </SpotlightCard>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function VisionMissionSection() {
  const { t } = useLanguage();
  return (
    <section data-testid="vision-mission-section" className="py-24 md:py-32 bg-[#080B10] border-t border-slate-800/60">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {['vision', 'mission'].map((key) => (
            <div key={key} className="rounded-3xl bg-[#0E131F] border border-slate-800/60 p-10 md:p-12" data-testid={`${key}-card`}>
              <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#6366F1] mb-4">
                {t(`about.${key}_label`)}
              </p>
              <p className="text-xl md:text-2xl font-semibold text-[#F8FAFC] leading-relaxed" style={HEADING_FONT}>
                {t(`about.${key}`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ValuesSection() {
  const { t, lang } = useLanguage();
  const mockCaption = { en: 'Illustrative example', 'zh-TW': '示意範例', 'zh-CN': '示意示例' };
  const chips = ['UCP 600', 'ISBP 821 (2023)', 'US SUBCONTRACT CLAUSES', 'ENCRYPTED IN TRANSIT (HTTPS)'];
  const cores = [
    { name: 'VOWPACE CORE', color: '#F59E0B' },
    { name: 'LCSHIELD CORE', color: '#6366F1' },
  ];
  return (
    <section data-testid="values-section" className="py-24 md:py-32 bg-[#080B10] border-t border-slate-800/60">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="text-center mb-14">
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#818CF8] mb-4">
            {t('about.terminal_label')}
          </p>
        </Reveal>
        <Reveal>
        <SpotlightCard className="rounded-2xl max-w-4xl mx-auto">
        <div data-testid="trust-terminal" className="relative rounded-2xl bg-[#0E131F] border border-slate-800/60 overflow-hidden shadow-[0_24px_60px_rgba(0,0,0,0.4)]">
          <CornerAccents />
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800/60 bg-white/[0.02]">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
            </div>
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#64748B]">
              DZENT / TRUST_TERMINAL
            </span>
          </div>
          <div className="p-6 md:p-8 font-mono">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {cores.map((c) => (
                <div key={c.name} data-testid={`terminal-status-${c.name.split(' ')[0].toLowerCase()}`} className="flex items-center justify-between rounded-lg bg-white/[0.03] border border-slate-800/60 px-4 py-3">
                  <span className="text-xs font-bold tracking-widest text-[#94A3B8]">&gt; {c.name}</span>
                  <span className="flex items-center gap-2 text-[11px] font-bold" style={{ color: c.color }}>
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color, animation: 'pulse-dot 2s ease-in-out infinite' }} />
                    ONLINE
                  </span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mb-6">
              {chips.map((chip) => (
                <span
                  key={chip}
                  data-testid={`compliance-chip-${chip.split(' ')[0].toLowerCase()}`}
                  className="px-3 py-1.5 rounded text-[10px] font-bold tracking-widest text-[#818CF8] bg-[#6366F1]/[0.08] border border-[#6366F1]/25 hover:-translate-y-0.5 hover:border-[#6366F1]/60 hover:bg-[#6366F1]/[0.15] hover:shadow-[0_4px_16px_rgba(99,102,241,0.25)] transition-all duration-200 cursor-default"
                >
                  {chip}
                </span>
              ))}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-5">
              {VALUES.map((val, i) => (
                <div key={val.key} data-testid={`value-card-${i}`} className="flex items-center gap-2 rounded-lg bg-white/[0.02] border border-slate-800/60 px-3 py-2.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#F59E0B] flex-shrink-0" />
                  <span className="text-[11px] font-semibold text-[#F8FAFC] truncate">{t(`about.${val.key}`)}</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] tracking-wider text-[#64748B] flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-[#10B981]" />
              {t('about.terminal_note')}
            </p>
          </div>
        </div>
        </SpotlightCard>
        <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-widest text-[#64748B]">{mockCaption[lang] || mockCaption.en}</p>
        </Reveal>
      </div>
    </section>
  );
}

function TimelineSection() {
  const { t } = useLanguage();
  const milestones = ['tl1', 'tl2', 'tl3'];
  return (
    <section data-testid="timeline-section" className="py-24 md:py-32 bg-[#080B10] border-t border-slate-800/60">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#6366F1]">
            {t('about.timeline_label')}
          </p>
        </div>
        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6">
          <div className="hidden md:block absolute top-[9px] left-[16.66%] right-[16.66%] h-px bg-gradient-to-r from-[#6366F1]/60 via-[#6366F1]/30 to-white/10" />
          {milestones.map((key, i) => (
            <div key={key} data-testid={`timeline-item-${i}`} className="relative flex md:block gap-5">
              <div className="flex md:justify-center flex-col items-center md:items-center shrink-0">
                <span className={`relative z-10 w-[18px] h-[18px] rounded-full border-[3px] bg-[#080B10] ${i === 2 ? 'border-white/20' : 'border-[#6366F1]'}`} />
                <span className="md:hidden flex-1 w-px bg-white/10 mt-2" />
              </div>
              <div className="md:text-center md:mt-6 pb-2">
                <p className="text-2xl md:text-3xl font-bold text-[#6366F1] mb-2 tracking-tight" style={HEADING_FONT}>
                  {t(`about.${key}_year`)}
                </p>
                <h3 className="text-base md:text-lg font-semibold text-[#F8FAFC] mb-2" style={HEADING_FONT}>
                  {t(`about.${key}_title`)}
                </h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed md:px-2" style={BODY_FONT}>
                  {t(`about.${key}_desc`)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MetricsSection() {
  const { t } = useLanguage();
  const metrics = [
    { value: '2', labelKey: 'about.metric_products' },
    { value: 'HTTPS', labelKey: 'about.metric_privacy' },
    { value: '≈30s', labelKey: 'about.metric_langs' },
    { valueKey: 'about.metric_global_value', labelKey: 'about.metric_global' },
  ];
  return (
    <section data-testid="metrics-section" className="py-20 md:py-28 bg-[#080B10] border-t border-slate-800/60 relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(520px circle at 15% 20%, rgba(99,102,241,0.08), transparent 60%), radial-gradient(560px circle at 85% 80%, rgba(99,102,241,0.06), transparent 60%)',
        }}
      />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-14">
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#6366F1]">
            {t('about.metrics_label')}
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {metrics.map((m, i) => (
            <div
              key={m.labelKey}
              data-testid={`metric-card-${i}`}
              className="text-center p-6 md:p-8 rounded-2xl bg-[#0E131F] border border-slate-800/60 hover:border-[#6366F1]/40 hover:-translate-y-1 transition-all duration-300"
            >
              <p className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#6366F1] mb-3 tracking-tight" style={HEADING_FONT}>
                <CountUp value={m.value || t(m.valueKey)} />
              </p>
              <p className="text-xs md:text-sm text-[#94A3B8] leading-relaxed" style={BODY_FONT}>
                {t(m.labelKey)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactCTASection() {
  const { t } = useLanguage();
  return (
    <section data-testid="about-cta-section" className="py-20 md:py-28 bg-[#080B10]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="relative rounded-3xl bg-[#0E131F] border border-slate-800/60 px-8 py-14 md:px-16 md:py-20 text-center overflow-hidden">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(480px circle at 20% 30%, rgba(99,102,241,0.14), transparent 60%), radial-gradient(520px circle at 80% 70%, rgba(99,102,241,0.10), transparent 60%)',
            }}
          />
          <div className="relative z-10">
            <h2
              data-testid="about-cta-title"
              className="text-2xl md:text-3xl lg:text-4xl tracking-tight font-bold text-[#F8FAFC] mb-4"
              style={HEADING_FONT}
            >
              {t('about.cta_title')}
            </h2>
            <p className="text-sm md:text-base text-[#94A3B8] mb-8" style={BODY_FONT}>{t('about.cta_desc')}</p>
            <Link to="/contact">
              <Button
                data-testid="about-cta-button"
                className="h-12 px-8 rounded-full bg-[#6366F1] text-white font-bold text-sm hover:bg-white hover:text-[#080B10] transition-all shadow-lg shadow-[#6366F1]/25"
              >
                {t('about.cta_btn')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="pt-20 bg-[#080B10]">
      <SEOHead path="/about" />
      <StorySection />
      <ExpertiseSection />
      <VisionMissionSection />
      <ValuesSection />
      <TimelineSection />
      <MetricsSection />
      <ContactCTASection />
    </div>
  );
}
